import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useHistory, useLocation } from "react-router-dom";
import { Alert, Button, Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { faEye } from "@fortawesome/free-regular-svg-icons";
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { request } from "hooks/useOkoApi/useOkoApi";
import { colors, constants, logEducatorDashboard, LogLevel } from "utils";
import { logError, LogCategory } from "utils/sentryLogger";
import { ViewQuestionsModal } from "./ViewQuestionsModal";
import { BrowseRelationshipsModal } from "./BrowseRelationshipsModal";
import { ClearGroupCodesModal } from "./ClearGroupCodesModal";
import { SelectDropdown } from "components/shared/FilterDropdowns/FilterDropdowns";

const DragHandleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="8" height="16" viewBox="0 0 8 16" fill="none" aria-hidden="true">
    <path d="M1 9C1.55228 9 2 8.55228 2 8C2 7.44772 1.55228 7 1 7C0.447715 7 0 7.44772 0 8C0 8.55228 0.447715 9 1 9Z" fill="white" stroke="white"/>
    <path d="M1 2C1.55228 2 2 1.55228 2 1C2 0.447715 1.55228 0 1 0C0.447715 0 0 0.447715 0 1C0 1.55228 0.447715 2 1 2Z" fill="white" stroke="white"/>
    <path d="M1 16C1.55228 16 2 15.5523 2 15C2 14.4477 1.55228 14 1 14C0.447715 14 0 14.4477 0 15C0 15.5523 0.447715 16 1 16Z" fill="white" stroke="white"/>
    <path d="M7 9C7.55228 9 8 8.55228 8 8C8 7.44772 7.55228 7 7 7C6.44772 7 6 7.44772 6 8C6 8.55228 6.44772 9 7 9Z" fill="white" stroke="white"/>
    <path d="M7 2C7.55228 2 8 1.55228 8 1C8 0.447715 7.55228 0 7 0C6.44772 0 6 0.447715 6 1C6 1.55228 6.44772 2 7 2Z" fill="white" stroke="white"/>
    <path d="M7 16C7.55228 16 8 15.5523 8 15C8 14.4477 7.55228 14 7 14C6.44772 14 6 14.4477 6 15C6 15.5523 6.44772 16 7 16Z" fill="white" stroke="white"/>
  </svg>
);

const ChipRemoveIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

const CirclePlusIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="M8 12h8" />
    <path d="M12 8v8" />
  </svg>
);

const EyeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const PieChartIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#002E19" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
    <path d="M22 12A10 10 0 0 0 12 2v10z" />
  </svg>
);

const InfoCircleIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#002E19" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const SESSION_LENGTH_OPTIONS = [
  { value: 12.5, label: "10-15 Minutes" },
  { value: 17.5, label: "15-20 Minutes" },
  { value: 22.5, label: "20-25 Minutes" },
  { value: 27.5, label: "25-30 Minutes" },
  { value: 32.5, label: "30-35 Minutes" },
];

const sessionLengthLabel = (mins) => {
  const exact = SESSION_LENGTH_OPTIONS.find((opt) => opt.value === mins);
  if (exact) return exact.label;
  const bucket = SESSION_LENGTH_OPTIONS.find((opt) => mins <= opt.value + 2.5);
  return bucket ? bucket.label : `${mins} Minutes`;
};

const formatLastUpdated = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US");
};

const PageWrap = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 64px);
  background: ${colors.milkGlass500};
`;

const Scroll = styled.div`
  flex: 1;
  padding: 0 0 120px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ScrollContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px 32px 0;
`;

const TrayCard = styled.div`
  background: white;
  padding: 16px 32px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

const TrayLabel = styled.span`
  color: #002E19;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
`;

const TrayClearLink = styled.button`
  background: transparent;
  border: none;
  color: #605DEC;
  cursor: pointer;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  padding: 0;
  text-decoration: underline;
`;

const Chip = styled.span`
  align-items: center;
  background: #605DEC;
  border-radius: 26px;
  color: white;
  display: inline-flex;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 700;
  gap: 8px;
  height: 40px;
  letter-spacing: 0.25px;
  line-height: 22px;
  padding: ${(p) => (p.$hasRemove ? "8px" : "8px 16px 8px 8px")};
  touch-action: none;
`;

const DragHandle = styled.span`
  align-items: center;
  background: transparent;
  border: none;
  padding: 0;
  color: white;
  cursor: grab;
  display: inline-flex;
  height: 24px;
  justify-content: center;
  width: 24px;
  touch-action: none;
  &:active {
    cursor: grabbing;
  }
  &:focus-visible {
    outline: 2px solid white;
    outline-offset: 1px;
    border-radius: 4px;
  }
`;

const ChipRemove = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  display: inline-flex;
  height: 24px;
  justify-content: center;
  padding: 0;
  width: 24px;
  &:hover {
    opacity: 0.8;
  }
`;

const StandardCardWrap = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0px 1px 2px 0px rgba(14, 26, 49, 0.05);
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: hidden;
  padding: 16px 0;
`;

const HeaderPad = styled.div`
  padding: 0 24px;
`;

const SectionDivider = styled.div`
  border-top: 1px solid #EEECE7;
`;

const StandardHeaderRow = styled.div`
  align-items: center;
  display: flex;
  gap: 24px;
  justify-content: space-between;
`;

const StandardCodeLabel = styled.div`
  color: #BA4A21;
  font-family: "Roboto", sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const StandardName = styled.div`
  color: #002E19;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.25px;
  line-height: 26px;
`;

const MetaRow = styled.div`
  color: #002E19;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

const SessionRow = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
`;

const ViewChangeLink = styled.button`
  background: transparent;
  border: none;
  color: #605DEC;
  cursor: pointer;
  font-size: 18px;
  font-weight: 400;
  letter-spacing: 0.25px;
  line-height: 26px;
  padding: 0 16px;
`;

const SectionWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 32px;
`;

const SectionHeader = styled.div`
  align-items: baseline;
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
`;

const SectionTitle = styled.div`
  color: #002E19;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.25px;
  line-height: 26px;
`;

const SectionLink = styled.button`
  background: transparent;
  border: none;
  color: #605DEC;
  cursor: pointer;
  font-size: 18px;
  font-weight: 400;
  letter-spacing: 0.25px;
  line-height: 26px;
  padding: 0 16px;
`;

const LevelLabel = styled.div`
  color: #002E19;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  margin: 12px 0 8px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
`;

const StandardCellWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TitleWrap = styled.div`
  position: relative;
  width: 100%;
`;

const TitleClamp = styled.div`
  color: #002E19;
  display: -webkit-box;
  font-size: 16px;
  font-weight: 700;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  letter-spacing: 0.25px;
  line-height: 22px;
  min-height: 44px;
  overflow: hidden;
`;

const TitleTooltip = styled.span`
  background: #EAEAFC;
  border-radius: 20px;
  bottom: calc(100% + 8px);
  color: #00140B;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  left: 50%;
  line-height: 20px;
  max-width: 320px;
  opacity: 0;
  padding: 16px;
  pointer-events: none;
  position: absolute;
  transform: translateX(-50%);
  transition: opacity 0.15s ease-in;
  white-space: normal;
  z-index: 10;
  ${TitleWrap}:hover & {
    opacity: 1;
  }
`;

const TitleTooltipArrow = styled.svg`
  display: block;
  left: 50%;
  position: absolute;
  top: 100%;
  transform: translateX(-50%);
`;

const StandardTitle = ({ children }) => {
  const ref = useRef(null);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    if (ref.current) {
      setOverflowing(ref.current.scrollHeight - ref.current.clientHeight > 1);
    }
  }, [children]);

  return (
    <TitleWrap>
      <TitleClamp ref={ref}>{children}</TitleClamp>
      {overflowing && (
        <TitleTooltip role="tooltip">
          {children}
          <TitleTooltipArrow width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 0V12L16 0H0Z" fill="#EAEAFC" />
          </TitleTooltipArrow>
        </TitleTooltip>
      )}
    </TitleWrap>
  );
};

const ViewQuestionsBtn = styled.button`
  align-items: center;
  align-self: flex-start;
  background: transparent;
  border: none;
  color: #605DEC;
  cursor: pointer;
  display: inline-flex;
  font-size: 14px;
  font-weight: 400;
  gap: 4px;
  line-height: 20px;
  padding: 0;
`;

const CodesLabel = styled.div`
  color: #002E19;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  margin-top: 4px;
`;

const CodesBox = styled.div`
  align-items: flex-start;
  background: #F4F4FD;
  border: 3px dashed #605DEC;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 170px;
  padding: 16px;
  position: relative;
`;

const AddCodeBtn = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  color: ${colors.blue700};
  cursor: pointer;
  display: inline-flex;
  font-size: 14px;
  font-weight: 400;
  gap: 4px;
  line-height: 20px;
  padding: 0 16px;
  height: 48px;
  &:disabled {
    color: ${colors.milkGlass800};
    cursor: not-allowed;
  }
`;

const PopoverWrap = styled.div`
  background: white;
  border: 1px solid ${colors.milkGlass600};
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(14, 26, 49, 0.12);
  max-height: 240px;
  min-width: 160px;
  overflow-y: auto;
  padding: 6px 0;
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 10;
`;

const PopoverItem = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  display: block;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 14px;
  text-align: left;
  width: 100%;
  &:hover {
    background: ${colors.milkGlass500};
  }
`;

const Footer = styled.div`
  align-items: center;
  background: #FFFEFE;
  border-top: 1px solid #EAEAFC;
  bottom: 0;
  box-shadow: 0px -1px 1.5px rgba(0, 0, 0, 0.05);
  display: flex;
  gap: 24px;
  justify-content: flex-end;
  left: 312px;
  padding: 16px 32px;
  position: fixed;
  right: 0;
  z-index: 5;
`;

const FooterRight = styled.div`
  align-items: center;
  display: flex;
  gap: 24px;
`;

const CancelBtn = styled(Button)`
  align-items: center;
  background: white;
  border: 1px solid #605DEC;
  border-radius: 8px;
  color: #605DEC;
  display: inline-flex;
  font-family: "Inter", sans-serif;
  font-size: 18px;
  font-weight: 700;
  height: 48px;
  justify-content: center;
  letter-spacing: 0.25px;
  line-height: 26px;
  padding: 0 16px;
  width: 180px;
  &:hover {
    background: #F4F4FD;
    border-color: #605DEC;
    color: #605DEC;
  }
`;

const DoneBtn = styled(Button)`
  align-items: center;
  background: #605DEC;
  border: none;
  border-radius: 8px;
  color: #FAF7F2;
  display: inline-flex;
  font-family: "Inter", sans-serif;
  font-size: 18px;
  font-weight: 700;
  height: 48px;
  justify-content: center;
  letter-spacing: 0.25px;
  line-height: 26px;
  padding: 0 16px;
  width: 180px;
  &:hover {
    background: #4E4BC4;
  }
  &:disabled {
    background: #D5D3CE;
  }
`;

const codesForStandard = (workingAssignments, trackType, standardCode) =>
  workingAssignments
    .filter((a) => a.track_type === trackType && a.standard_code === standardCode)
    .map((a) => a.group_code);

const buildStandardNameMap = (assignment, relationships) => {
  const map = new Map();
  if (assignment?.primary_standard_code) {
    map.set(assignment.primary_standard_code, assignment.primary_standard_name || "");
  }
  const walk = (groups) => {
    (groups || []).forEach((g) => {
      (g?.standards || []).forEach((s) => {
        if (s?.code) map.set(s.code, s?.name || s?.description || "");
      });
    });
  };
  walk(relationships?.prerequisites);
  walk(relationships?.extensions);
  return map;
};

const TRAY_DROP_ID = "tray";
const trackDropId = (trackType, standardCode) => `target:${trackType}:${standardCode}`;
const parseTrackDropId = (id) => {
  if (!id || !id.startsWith("target:")) return null;
  const idx = id.indexOf(":", "target:".length);
  if (idx < 0) return null;
  return {
    trackType: id.slice("target:".length, idx),
    standardCode: id.slice(idx + 1),
  };
};

const DraggableChip = ({ groupCode, onRemove, showRemove = true }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `code:${groupCode}`,
  });
  const style = {
    transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 1000 : undefined,
    position: isDragging ? "relative" : undefined,
  };
  // The drag handle carries the dnd-kit node ref + listeners/attributes, so the
  // remove button is a SIBLING of the draggable element rather than nested
  // inside it (avoids the axe nested-interactive violation).
  return (
    <Chip style={style} $hasRemove={showRemove}>
      <DragHandle
        as="button"
        type="button"
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        aria-label={`Drag ${groupCode}`}
      >
        <DragHandleIcon />
      </DragHandle>
      {groupCode.toUpperCase()}
      {showRemove && (
        <ChipRemove onClick={() => onRemove(groupCode)} aria-label={`Remove ${groupCode}`}>
          <ChipRemoveIcon />
        </ChipRemove>
      )}
    </Chip>
  );
};

const DroppableTray = ({ children, isDragging }) => {
  const { setNodeRef, isOver } = useDroppable({ id: TRAY_DROP_ID });
  return (
    <TrayCard
      ref={setNodeRef}
      style={
        isOver
          ? { background: colors.blue50, boxShadow: `0 0 0 2px ${colors.blue500} inset` }
          : isDragging
          ? { boxShadow: `0 0 0 1px ${colors.blue200} inset` }
          : undefined
      }
    >
      {children}
    </TrayCard>
  );
};

const DroppableCodesBox = ({ id, children, isEmpty }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  const emptyStyle = isEmpty
    ? { alignItems: "center", justifyContent: "center" }
    : undefined;
  const overStyle = isOver
    ? { borderColor: colors.blue500, background: colors.blue50 }
    : undefined;
  return (
    <CodesBox ref={setNodeRef} style={{ ...emptyStyle, ...overStyle }}>
      {children}
    </CodesBox>
  );
};

export const EditGroupsPage = () => {
  const history = useHistory();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const classroomId = urlParams.get("classroomId");
  const classIdFromUrl = urlParams.get("classId");
  const assignmentIdFromUrl = urlParams.get("assignmentId");

  const [bundle, setBundle] = useState(null);
  const [totalAvailable, setTotalAvailable] = useState(0);
  const [sessionLength, setSessionLength] = useState(0);
  const [relationships, setRelationships] = useState(null);
  const [workingAssignments, setWorkingAssignments] = useState([]);
  const [workingUnassigned, setWorkingUnassigned] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isClearModalOpen, setIsClearModalOpen] = useState(false);
  const [viewQuestionsTarget, setViewQuestionsTarget] = useState(null);
  const [viewAllInitialTab, setViewAllInitialTab] = useState(null);

  const goBackToGroups = useCallback(() => {
    const path = constants.EDUCATOR_PATH;
    history.push(classIdFromUrl ? `${path}?classId=${classIdFromUrl}` : path);
  }, [history, classIdFromUrl]);

  useEffect(() => {
    if (!classroomId) {
      setError("Missing classroom id.");
      setIsLoading(false);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const wrapper = await request(`groups/classroom/${classroomId}/assignment`, { method: "GET" });
        if (cancelled) return;
        const bundles = wrapper?.assignments || [];
        const matched = assignmentIdFromUrl
          ? bundles.find((b) => b.assignment_id === assignmentIdFromUrl)
          : bundles[0];
        if (!matched) {
          setError("Bundle not found.");
          return;
        }
        setBundle(matched);
        setTotalAvailable(wrapper?.total_available || 0);
        setSessionLength(wrapper?.session_length || 0);
        setWorkingAssignments(matched.assignments || []);
        // Edit-tray pool = classroom-wide unassigned (codes not on any CLA).
        // Codes on OTHER bundles are intentionally hidden — moving them here
        // would trigger the BE's cross-bundle reject.
        setWorkingUnassigned(wrapper?.unassigned_codes || []);
        if (matched.primary_standard_code) {
          const r = await request(`standards/${matched.primary_standard_code}/relationships`, { method: "GET" });
          if (cancelled) return;
          setRelationships(r);
        }
      } catch (e) {
        if (cancelled) return;
        logError("EditGroupsPage failed to load", e, {
          category: LogCategory.ERROR,
          classroomId,
          assignmentId: assignmentIdFromUrl,
        });
        setError(e?.detail || e?.message || "Failed to load group assignments.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [classroomId, assignmentIdFromUrl]);

  const assignedCount = workingAssignments.length;
  const unassignedCount = workingUnassigned.length;

  const removeChip = (groupCode) => {
    setWorkingAssignments((prev) => prev.filter((a) => a.group_code !== groupCode));
    setWorkingUnassigned((prev) => (prev.includes(groupCode) ? prev : [...prev, groupCode]));
  };

  const assignChip = (groupCode, trackType, standardCode, standardName) => {
    setWorkingUnassigned((prev) => prev.filter((c) => c !== groupCode));
    setWorkingAssignments((prev) => [
      ...prev.filter((a) => a.group_code !== groupCode),
      { group_code: groupCode, track_type: trackType, standard_code: standardCode, standard_name: standardName },
    ]);
  };

  const openClearModal = () => setIsClearModalOpen(true);

  // Release the selected codes immediately on confirm (not on Save): PUT
  // track_type:null for this CLA, then reflect it in the working state (so other
  // in-progress edits are preserved and no full reload is needed).
  const handleClearConfirm = async (selected) => {
    setIsClearModalOpen(false);
    const codes = workingAssignments.map((a) => a.group_code).filter((code) => selected.has(code));
    if (!codes.length || !bundle?.assignment_id) return;
    try {
      await request(`groups/classrooms/${classroomId}/assignments/${bundle.assignment_id}`, {
        method: "PUT",
        payload: { assignments: codes.map((code) => ({ group_code: code, track_type: null, standard_code: null })) },
      });
      setWorkingUnassigned((prev) => [...prev, ...codes]);
      setWorkingAssignments((prev) => prev.filter((a) => !selected.has(a.group_code)));
    } catch (e) {
      logEducatorDashboard("EditGroupsPage: failed to clear group codes", { level: LogLevel.WARNING, statusCode: e?.status });
    }
  };

  const handleSessionLengthChange = async (newValue) => {
    setSessionLength(newValue);
    try {
      await Promise.all((bundle?.assignments || []).map((a) => (
        request(`group/${a.group_code}`, {
          method: "POST",
          payload: {
            config: {
              standard_prefix: a.standard_code,
              activity_type: constants.CONSENSUS_ENUM,
              classroom_id: classroomId,
              session_length: newValue,
            },
          },
        })
      )));
    } catch (e) {
      logError("EditGroupsPage failed to update session length", e, {
        category: LogCategory.ERROR,
        classroomId,
      });
      setError(e?.detail || e?.message || "Failed to update session length.");
    }
  };

  const standardNameMap = useMemo(
    () => buildStandardNameMap(bundle, relationships),
    [bundle, relationships]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  const [activeDragId, setActiveDragId] = useState(null);
  const handleDragStart = (event) => setActiveDragId(event.active?.id || null);
  const handleDragCancel = () => setActiveDragId(null);
  const handleDragEnd = (event) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over || !active) return;
    if (typeof active.id !== "string" || !active.id.startsWith("code:")) return;
    const groupCode = active.id.slice("code:".length);
    const overId = over.id;
    if (overId === TRAY_DROP_ID) {
      removeChip(groupCode);
      return;
    }
    const target = parseTrackDropId(overId);
    if (!target) return;
    const standardName = standardNameMap.get(target.standardCode) || "";
    assignChip(groupCode, target.trackType, target.standardCode, standardName);
  };

  const handleDone = async () => {
    if (!bundle?.assignment_id) return;
    setSaving(true);
    setError(null);
    try {
      // Only send updates that actually changed *within this bundle*.
      // - Codes already on this CLA whose track/standard didn't change → skip.
      // - Codes added this session → assign update.
      // - Codes removed from this CLA in this session → unassign update.
      // Codes that started classroom-wide unassigned and stayed that way →
      // skip (no change).
      const originalById = new Map(
        (bundle.assignments || []).map((a) => [a.group_code, a]),
      );
      const workingById = new Map(
        workingAssignments.map((a) => [a.group_code, a]),
      );

      const updates = [];
      // Reassign / new-on-this-CLA
      workingAssignments.forEach((wa) => {
        const orig = originalById.get(wa.group_code);
        if (
          !orig ||
          orig.track_type !== wa.track_type ||
          orig.standard_code !== wa.standard_code
        ) {
          updates.push({
            group_code: wa.group_code,
            track_type: wa.track_type,
            standard_code: wa.standard_code,
          });
        }
      });
      // Removed from this CLA → release back to pool
      (bundle.assignments || []).forEach((orig) => {
        if (!workingById.has(orig.group_code)) {
          updates.push({
            group_code: orig.group_code,
            track_type: null,
            standard_code: null,
          });
        }
      });

      if (updates.length === 0) {
        goBackToGroups();
        return;
      }

      await request(
        `groups/classrooms/${classroomId}/assignments/${bundle.assignment_id}`,
        { method: "PUT", payload: { assignments: updates } },
      );
      logEducatorDashboard("EditGroupsPage saved", {
        level: LogLevel.INFO,
        event: "edit_groups_save",
        classroomId,
        assignmentId: bundle.assignment_id,
        assignedCount,
        unassignedCount,
      });
      goBackToGroups();
    } catch (e) {
      logError("EditGroupsPage save failed", e, {
        category: LogCategory.ERROR,
        classroomId,
        assignmentId: bundle?.assignment_id,
      });
      setError(e?.detail || e?.message || "Failed to save group assignments.");
    } finally {
      setSaving(false);
    }
  };

  const lastUpdated = formatLastUpdated(bundle?.last_updated_at);

  if (isLoading) {
    return (
      <PageWrap>
        <Scroll>
          <div style={{ padding: 48, textAlign: "center" }}>
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          </div>
        </Scroll>
      </PageWrap>
    );
  }

  if (error && !bundle) {
    return (
      <PageWrap>
        <Scroll>
          <Alert variant="danger">{error}</Alert>
          <CancelBtn onClick={goBackToGroups}>Back to Groups</CancelBtn>
        </Scroll>
      </PageWrap>
    );
  }

  if (!bundle?.primary_standard_code) {
    return (
      <PageWrap>
        <Scroll>
          <Alert variant="warning">
            This class does not have a standard assigned yet. Open Standards to assign one.
          </Alert>
          <CancelBtn onClick={goBackToGroups}>Back to Groups</CancelBtn>
        </Scroll>
      </PageWrap>
    );
  }

  const primaryCode = bundle.primary_standard_code;
  const primaryName = bundle.primary_standard_name;

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
    <PageWrap>
      <Scroll>
        <DroppableTray isDragging={Boolean(activeDragId)}>
          <TrayLabel>Unassigned Group Codes:</TrayLabel>
          {workingUnassigned.length === 0 ? (
            <TrayClearLink onClick={openClearModal}>Clear All Group Codes</TrayClearLink>
          ) : (
            workingUnassigned.map((code) => (
              <DraggableChip key={code} groupCode={code} showRemove={false} />
            ))
          )}
        </DroppableTray>

        <ScrollContent>
        <StandardCardWrap>
          <HeaderPad>
            <StandardHeaderRow>
              <div style={{ display: "flex", flex: 1, gap: 8, minWidth: 0 }}>
                <PieChartIcon />
                <div style={{ display: "flex", flexDirection: "column", flex: 1, gap: 8, minWidth: 0 }}>
                  <div>
                    <StandardCodeLabel>STANDARD - {primaryCode}</StandardCodeLabel>
                    <StandardName>{primaryName}</StandardName>
                  </div>
                  {(lastUpdated || assignedCount > 0) && (
                    <MetaRow>
                      {lastUpdated && <>Last updated: {lastUpdated}</>}
                      {lastUpdated && assignedCount > 0 && <span style={{ color: "#CBCDCC", margin: "0 8px" }}>|</span>}
                      {assignedCount > 0 && <>{assignedCount} Group Code{assignedCount === 1 ? "" : "s"}</>}
                    </MetaRow>
                  )}
                  <SessionRow>
                    <div style={{ alignItems: "center", display: "flex", gap: 4 }}>
                      <InfoCircleIcon />
                      <span style={{ color: "#002E19", fontSize: 14, fontWeight: 700, lineHeight: "20px" }}>Session Length:</span>
                    </div>
                    <div style={{ width: 175 }}>
                      <SelectDropdown
                        value={sessionLength}
                        displayText={sessionLengthLabel(sessionLength)}
                        options={SESSION_LENGTH_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
                        onChange={handleSessionLengthChange}
                      />
                    </div>
                  </SessionRow>
                </div>
              </div>
              <div style={{ alignItems: "center", display: "flex", flexShrink: 0, gap: 8 }}>
                <ViewChangeLink
                  onClick={() => {
                    const params = new URLSearchParams({
                      classId: classIdFromUrl || "",
                      classroomId,
                      assignmentId: bundle.assignment_id,
                      currentStandardCode: primaryCode,
                    });
                    history.push(`${constants.VIEW_CHANGE_STANDARD_PATH}?${params.toString()}`);
                  }}
                >
                  View &amp; Change Standard
                </ViewChangeLink>
                <button
                  aria-label="Collapse"
                  onClick={goBackToGroups}
                  style={{
                    alignItems: "center",
                    backgroundColor: "#605DEC",
                    border: "none",
                    borderRadius: 6,
                    cursor: "pointer",
                    display: "flex",
                    height: 40,
                    justifyContent: "center",
                    padding: 0,
                    width: 40,
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M14.25 9V4.5C14.25 4.30109 14.329 4.11032 14.4697 3.96967C14.6103 3.82902 14.8011 3.75 15 3.75C15.1989 3.75 15.3897 3.82902 15.5303 3.96967C15.671 4.11032 15.75 4.30109 15.75 4.5V8.25H19.5C19.6989 8.25 19.8897 8.32902 20.0303 8.46967C20.171 8.61032 20.25 8.80109 20.25 9C20.25 9.19891 20.171 9.38968 20.0303 9.53033C19.8897 9.67098 19.6989 9.75 19.5 9.75H15C14.8011 9.75 14.6103 9.67098 14.4697 9.53033C14.329 9.38968 14.25 9.19891 14.25 9ZM9 14.25H4.5C4.30109 14.25 4.11032 14.329 3.96967 14.4697C3.82902 14.6103 3.75 14.8011 3.75 15C3.75 15.1989 3.82902 15.3897 3.96967 15.5303C4.11032 15.671 4.30109 15.75 4.5 15.75H8.25V19.5C8.25 19.6989 8.32902 19.8897 8.46967 20.0303C8.61032 20.171 8.80109 20.25 9 20.25C9.19891 20.25 9.38968 20.171 9.53033 20.0303C9.67098 19.8897 9.75 19.6989 9.75 19.5V15C9.75 14.8011 9.67098 14.6103 9.53033 14.4697C9.38968 14.329 9.19891 14.25 9 14.25ZM19.5 14.25H15C14.8011 14.25 14.6103 14.329 14.4697 14.4697C14.329 14.6103 14.25 14.8011 14.25 15V19.5C14.25 19.6989 14.329 19.8897 14.4697 20.0303C14.6103 20.171 14.8011 20.25 15 20.25C15.1989 20.25 15.3897 20.171 15.5303 20.0303C15.671 19.8897 15.75 19.6989 15.75 19.5V15.75H19.5C19.6989 15.75 19.8897 15.671 20.0303 15.5303C20.171 15.3897 20.25 15.1989 20.25 15C20.25 14.8011 20.171 14.6103 20.0303 14.4697C19.8897 14.329 19.6989 14.25 19.5 14.25ZM9 3.75C8.80109 3.75 8.61032 3.82902 8.46967 3.96967C8.32902 4.11032 8.25 4.30109 8.25 4.5V8.25H4.5C4.30109 8.25 4.11032 8.32902 3.96967 8.46967C3.82902 8.61032 3.75 8.80109 3.75 9C3.75 9.19891 3.82902 9.38968 3.96967 9.53033C4.11032 9.67098 4.30109 9.75 4.5 9.75H9C9.19891 9.75 9.38968 9.67098 9.53033 9.53033C9.67098 9.38968 9.75 9.19891 9.75 9V4.5C9.75 4.30109 9.67098 4.11032 9.53033 3.96967C9.38968 3.82902 9.19891 3.75 9 3.75Z" fill="white"/>
                  </svg>
                </button>
              </div>
            </StandardHeaderRow>
          </HeaderPad>

          <SectionDivider />

          <CoreSection
            primaryCode={primaryCode}
            primaryName={primaryName}
            codes={codesForStandard(workingAssignments, "core", primaryCode)}
            unassigned={workingUnassigned}
            onRemove={removeChip}
            onAdd={(code) => assignChip(code, "core", primaryCode, primaryName)}
            onViewQuestions={() => setViewQuestionsTarget({ code: primaryCode, name: primaryName })}
            onViewAll={() => setViewAllInitialTab("core")}
          />

          <SectionDivider />

          <LevelGroupSection
            title="Prerequisites"
            trackType="prerequisites"
            levels={relationships?.prerequisites || []}
            workingAssignments={workingAssignments}
            unassigned={workingUnassigned}
            onRemove={removeChip}
            onAdd={(code, standardCode, standardName) =>
              assignChip(code, "prerequisites", standardCode, standardName)
            }
            onViewQuestions={(s) => setViewQuestionsTarget(s)}
            onViewAll={() => setViewAllInitialTab("prerequisites")}
          />

          <SectionDivider />

          <LevelGroupSection
            title="Extensions"
            trackType="extensions"
            levels={relationships?.extensions || []}
            workingAssignments={workingAssignments}
            unassigned={workingUnassigned}
            onRemove={removeChip}
            onAdd={(code, standardCode, standardName) =>
              assignChip(code, "extensions", standardCode, standardName)
            }
            onViewQuestions={(s) => setViewQuestionsTarget(s)}
            onViewAll={() => setViewAllInitialTab("extensions")}
          />
        </StandardCardWrap>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        </ScrollContent>
      </Scroll>

      <Footer>
        <FooterRight>
          <CancelBtn onClick={goBackToGroups} disabled={saving}>Cancel</CancelBtn>
          <DoneBtn onClick={handleDone} disabled={saving}>
            {saving ? <FontAwesomeIcon icon={faSpinner} spin /> : "Done"}
          </DoneBtn>
        </FooterRight>
      </Footer>

      <ViewQuestionsModal
        isOpen={Boolean(viewQuestionsTarget)}
        onClose={() => setViewQuestionsTarget(null)}
        standardCode={viewQuestionsTarget?.code}
        standardName={viewQuestionsTarget?.name}
      />
      <BrowseRelationshipsModal
        isOpen={Boolean(viewAllInitialTab)}
        onClose={() => setViewAllInitialTab(null)}
        primaryStandardCode={primaryCode}
        primaryStandardName={primaryName}
        initialTab={viewAllInitialTab || "prerequisites"}
      />
      <ClearGroupCodesModal
        isOpen={isClearModalOpen}
        onClose={() => setIsClearModalOpen(false)}
        clearable={workingAssignments.map((a) => ({
          group_code: a.group_code,
          standard_code: a.standard_code,
          standard_name: a.standard_name,
        }))}
        defaultSelectAll
        onConfirm={handleClearConfirm}
      />
    </PageWrap>
    </DndContext>
  );
};

const CoreSection = ({ primaryCode, primaryName, codes, unassigned, onRemove, onAdd, onViewQuestions, onViewAll }) => (
  <SectionWrap>
    <SectionHeader>
      <SectionTitle>Core Standard</SectionTitle>
      <SectionLink onClick={onViewAll}>View All</SectionLink>
    </SectionHeader>
    <StandardCellWrap>
      <div style={{ color: "#BA4A21", fontFamily: "'ABC Marfa', sans-serif", fontSize: 12, fontWeight: 600, letterSpacing: "0.5px", lineHeight: "18px", textTransform: "uppercase" }}>
        Standard - {primaryCode}
      </div>
      <StandardTitle>{primaryName}</StandardTitle>
      <ViewQuestionsBtn onClick={() => onViewQuestions({ code: primaryCode, name: primaryName })}>
        <EyeIcon />
        View Questions
      </ViewQuestionsBtn>
      <div style={{ alignItems: "flex-start", display: "flex", gap: 16, width: "100%" }}>
        <CodesLabel style={{ marginTop: 0, paddingTop: 16 }}>Group Codes:</CodesLabel>
        <div style={{ flex: 1, minWidth: 0 }}>
          <DroppableCodesBox id={trackDropId("core", primaryCode)} isEmpty={codes.length === 0}>
            {codes.map((c) => (
              <DraggableChip key={c} groupCode={c} onRemove={onRemove} />
            ))}
            <AddCodePopover unassigned={unassigned} onPick={(c) => onAdd(c)} />
          </DroppableCodesBox>
        </div>
      </div>
    </StandardCellWrap>
  </SectionWrap>
);

const LevelGroupSection = ({ title, trackType, levels, workingAssignments, unassigned, onRemove, onAdd, onViewQuestions, onViewAll }) => {
  const hasContent = (levels || []).some((g) => (g?.standards || []).length > 0);
  return (
    <SectionWrap>
      <SectionHeader>
        <SectionTitle>{title}:</SectionTitle>
        <SectionLink onClick={onViewAll}>View All</SectionLink>
      </SectionHeader>
      {!hasContent && (
        <div style={{ color: colors.milkGlass950, fontSize: 14, fontStyle: "italic" }}>
          No {title.toLowerCase()} available for this standard.
        </div>
      )}
      {(levels || []).map((group) => {
        const stds = group?.standards || [];
        if (stds.length === 0) return null;
        return (
          <div key={`level-${group.level}`}>
            <LevelLabel>Level {group.level} {title}</LevelLabel>
            <Grid>
              {stds.map((s) => (
                <StandardCell
                  key={s.code}
                  trackType={trackType}
                  standardCode={s.code}
                  name={s.name || s.description || ""}
                  codes={codesForStandard(workingAssignments, trackType, s.code)}
                  unassigned={unassigned}
                  onRemove={onRemove}
                  onAdd={(c) => onAdd(c, s.code, s.name || s.description || "")}
                  onViewQuestions={() => onViewQuestions({ code: s.code, name: s.name || s.description || "" })}
                />
              ))}
            </Grid>
          </div>
        );
      })}
    </SectionWrap>
  );
};

const StandardCell = ({ trackType, standardCode, name, codes, unassigned, onRemove, onAdd, onViewQuestions }) => (
  <StandardCellWrap>
    <div style={{ color: "#BA4A21", fontFamily: "'ABC Marfa', sans-serif", fontSize: 12, fontWeight: 600, letterSpacing: "0.5px", lineHeight: "18px", textTransform: "uppercase" }}>
      Standard - {standardCode}
    </div>
    <StandardTitle>{name}</StandardTitle>
    <ViewQuestionsBtn onClick={onViewQuestions}>
      <EyeIcon />
      View Questions
    </ViewQuestionsBtn>
    <CodesLabel>Group Codes:</CodesLabel>
    <DroppableCodesBox id={trackDropId(trackType, standardCode)} isEmpty={codes.length === 0}>
      {codes.map((c) => (
        <DraggableChip key={c} groupCode={c} onRemove={onRemove} />
      ))}
      <AddCodePopover unassigned={unassigned} onPick={onAdd} />
    </DroppableCodesBox>
  </StandardCellWrap>
);

const AddCodePopover = ({ unassigned, onPick }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [open]);

  const disabled = unassigned.length === 0;

  return (
    <div style={{ position: "relative" }} ref={wrapRef}>
      <AddCodeBtn
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        title={disabled ? "No unassigned group codes available" : ""}
      >
        <CirclePlusIcon />
        Add Group Code
      </AddCodeBtn>
      {open && (
        <PopoverWrap>
          {unassigned.map((c) => (
            <PopoverItem
              key={c}
              onClick={() => {
                onPick(c);
                setOpen(false);
              }}
            >
              {c.toUpperCase()}
            </PopoverItem>
          ))}
        </PopoverWrap>
      )}
    </div>
  );
};

export const __testables = { codesForStandard, formatLastUpdated };
